// Modèle Excel « réappro » envoyé par e-mail (Worker) ou téléchargé (navigateur) depuis le
// calculateur de stock de sécurité. Mêmes formules que compute() et toExcel() (stockSecurite.js),
// écrites en anglais comme Excel les enregistre : elles s'affichent en français dans un Excel français.
// Les valeurs en cache viennent de compute() ; le classeur se recalcule de toute façon à l'ouverture.
import { CHANNELS, CONSEIL_SEMAINES, SERVICE_LEVELS, compute, filledWeeks, packOf, pos, seasonPct } from './stockSecurite';
import { buildXlsx, col } from './xlsx';

export const MODELE_NOM = 'modele-reappro-autoria.xlsx';
export const CATALOGUE_LIGNES = 50;

const IN = 5; // case à remplir
const IN_PCT = 6;
const B = 1;
const RES = 3;
const NUM = 2;
const TITLE = 7;

const service = (p) => Number((SERVICE_LEVELS.find((l) => l.value === p.service) || SERVICE_LEVELS[1]).value);

// Statut d'une ligne : ce qu'il faut traiter cette semaine d'abord.
export const statut = (r) => {
  if (r.position / r.d < r.lead) return 'Rupture probable';
  if (r.daysToOrder <= 7) return 'Commander cette semaine';
  if (r.stock / r.d > r.lead + r.cycle + 90) return 'Surstock';
  return 'OK';
};

// Onglet 1 : même disposition que « Copier pour Excel » (A–F ventes, H–I paramètres et résultats).
function produit(p, r) {
  const weeks = filledWeeks(p.weeks);
  const last = weeks.length + 1;
  const rows = [[{ v: 'Semaine', s: B }, ...CHANNELS.map((c) => ({ v: c, s: B })), { v: 'Total', s: B }, { v: 'Amazon + Shopify', s: B }]];
  weeks.forEach((w, i) => {
    const n = i + 2;
    rows.push([`S${i + 1}`, ...w.map((v) => ({ v, s: IN })), { f: `B${n}+C${n}+D${n}`, v: w[0] + w[1] + w[2] }, { f: `B${n}+C${n}`, v: w[0] + w[1] }]);
  });
  const params = [
    [{ v: 'Paramètre', s: B }, { v: 'Valeur', s: B }],
    ['Fabrication fournisseur (jours)', { v: pos(p.production), s: IN }],
    ['Transport (jours)', { v: pos(p.transport), s: IN }],
    ['Réception entrepôt ou Amazon (jours)', { v: pos(p.reception), s: IN }],
    ['Délai total (jours)', { f: 'SUM(I2:I4)', v: r.lead }],
    ['Délai total le plus court constaté (jours)', { v: pos(p.leadMin), s: IN }],
    ['Délai total le plus long constaté (jours)', { v: pos(p.leadMax), s: IN }],
    ['Niveau de service', { v: service(p), s: IN_PCT }],
    ['Hausse attendue sur la période couverte (%)', { v: seasonPct(p.season), s: IN }],
    ['Stock possédé (entrepôt, Amazon, envois vers Amazon)', { v: pos(p.stock), s: IN }],
    ['Commandé au fournisseur, pas encore reçu', { v: pos(p.transit), s: IN }],
    ['Période couverte par chaque commande (jours)', { v: pos(p.cycle), s: IN }],
    ['Minimum de commande (unités)', { v: pos(p.moq), s: IN }],
    ['Colisage (unités par carton)', { v: packOf(p.pack), s: IN }],
    ['Coût d\'achat rendu (€ HT par unité)', { v: pos(p.cost), s: IN }],
    [],
    ['Ventes par jour', { f: `AVERAGE(E2:E${last})/7`, v: r.d, s: NUM }],
    ['Écart-type des ventes par jour (hors B2B)', { f: `_xlfn.STDEV.S(F2:F${last})/SQRT(7)`, v: r.sdDay, s: NUM }],
    ['Écart-type du délai (jours)', { f: 'MAX(0,I7-I6)/3', v: r.leadSd, s: NUM }],
    [{ v: 'Stock de sécurité', s: B }, { f: 'ROUNDUP(_xlfn.NORM.S.INV(I8)*SQRT(I5*I18^2+I17^2*I19^2),0)', v: r.safety, s: RES }],
    [{ v: 'Point de commande', s: B }, { f: 'ROUNDUP(I17*I5+I20,0)', v: r.reorder, s: RES }],
    ['Couverture du stock en main (jours)', { f: 'ROUND(I10/I17,0)', v: Math.round(r.cover) }],
    [{ v: 'Jours avant de commander', s: B }, { f: 'MAX(0,ROUNDUP((I10+I11-I21)/I17,0))', v: r.daysToOrder, s: RES }],
    ['Quantité avant minimum et colisage', { f: 'MAX(0,ROUNDUP(I17*I5+I20+I17*(1+I9/100)*I12-MIN(I10+I11,I21),0))', v: r.qtyRaw }],
    [{ v: 'Quantité à commander', s: B }, { f: 'IF(I24<=0,0,ROUNDUP(MAX(I24,I13)/MAX(1,I14),0)*MAX(1,I14))', v: r.qty, s: RES }],
    ['Valeur du stock de sécurité (€ HT)', { f: 'I20*I15', v: r.safetyValue }],
    [],
    ['Cases en bleu clair : à modifier. Le reste se recalcule.']
  ];
  params.forEach((pr, i) => {
    if (!rows[i]) rows[i] = [];
    while (rows[i].length < 7) rows[i].push(null);
    rows[i][7] = pr[0] ?? null;
    rows[i][8] = pr[1] ?? null;
  });
  return { name: 'Votre produit', widths: [10, 10, 10, 10, 10, 16, 3, 50, 14], rows };
}

// Onglet 2 : une ligne par produit. Ventes Amazon + site sur 13 semaines, B2B en moyenne à part
// (il compte dans la demande, pas dans la variabilité), puis délais, stock et règles.
const SEM = CONSEIL_SEMAINES;
// Colonnes : A produit · B…N semaines · puis les champs ci-dessous, dans l'ordre.
const CHAMPS = ['b2b', 'lead', 'leadMin', 'leadMax', 'service', 'season', 'stock', 'transit', 'cycle', 'moq', 'pack', 'cost'];
const C = Object.fromEntries(CHAMPS.map((k, i) => [k, col(SEM + 1 + i)]));
const RES_COLS = ['d', 'sd', 'sdL', 'ss', 'pc', 'cover', 'days', 'raw', 'qty', 'status', 'value'];
const R = Object.fromEntries(RES_COLS.map((k, i) => [k, col(SEM + 1 + CHAMPS.length + i)]));

function catalogue(p) {
  const head = ['Produit', ...Array.from({ length: SEM }, (_, i) => `S${i + 1} Amazon + site`), 'B2B par semaine (moyenne)', 'Délai total (jours)', 'Délai le plus court (jours)', 'Délai le plus long (jours)', 'Niveau de service', 'Hausse sur la période couverte (%)', 'Stock possédé', 'Commandé, pas reçu', 'Période couverte (jours)', 'Minimum de commande', 'Colisage', 'Coût d\'achat (€ HT)', 'Ventes / jour', 'Écart-type / jour (hors B2B)', 'Écart-type du délai', 'Stock de sécurité', 'Point de commande', 'Couverture (jours)', 'Jours avant commande', 'Quantité brute', 'À commander', 'Statut', 'Valeur du stock de sécurité (€ HT)'];
  const rows = [head.map((v) => ({ v, s: B }))];
  // La 1re ligne reprend le produit du calculateur (ses 13 dernières semaines).
  const weeks = filledWeeks(p.weeks).slice(-SEM);
  const b2c = weeks.map((w) => w[0] + w[1]);
  const b2bMoy = weeks.length ? weeks.reduce((a, w) => a + w[2], 0) / weeks.length : 0;
  const r1 = compute({ ...p, weeks: weeks.map((w) => [String(w[0]), String(w[1]), String(w[2])]) });
  for (let i = 0; i < CATALOGUE_LIGNES; i++) {
    const n = i + 2;
    const first = i === 0 && !r1.error;
    const v = (x) => (first ? x : '');
    const cells = [
      { v: first ? 'Votre produit' : '', s: IN },
      ...Array.from({ length: SEM }, (_, k) => ({ v: first && k < b2c.length ? b2c[k] : '', s: IN })),
      { v: v(b2bMoy), s: IN }, { v: v(r1.lead), s: IN }, { v: v(pos(p.leadMin)), s: IN }, { v: v(pos(p.leadMax)), s: IN },
      { v: first ? service(p) : 0.95, s: IN_PCT }, { v: first ? seasonPct(p.season) : 0, s: IN }, { v: v(r1.stock), s: IN },
      { v: v(r1.transit), s: IN }, { v: v(r1.cycle), s: IN }, { v: v(r1.moq), s: IN }, { v: first ? r1.pack : 1, s: IN }, { v: v(r1.cost || ''), s: IN }
    ];
    const W = `B${n}:${col(SEM)}${n}`;
    const e = (c) => `${c}${n}`;
    const d = e(R.d);
    const blank = `${d}=""`;
    cells.push(
      { f: `IF(COUNT(${W})<2,"",(AVERAGE(${W})+${e(C.b2b)})/7)`, v: v(r1.d), s: NUM },
      { f: `IF(${blank},"",_xlfn.STDEV.S(${W})/SQRT(7))`, v: v(r1.sdDay), s: NUM },
      { f: `IF(${blank},"",MAX(0,${e(C.leadMax)}-${e(C.leadMin)})/3)`, v: v(r1.leadSd), s: NUM },
      { f: `IF(${blank},"",ROUNDUP(_xlfn.NORM.S.INV(${e(C.service)})*SQRT(${e(C.lead)}*${e(R.sd)}^2+${d}^2*${e(R.sdL)}^2),0))`, v: v(r1.safety), s: RES },
      { f: `IF(${blank},"",ROUNDUP(${d}*${e(C.lead)}+${e(R.ss)},0))`, v: v(r1.reorder), s: RES },
      { f: `IF(${blank},"",ROUND(${e(C.stock)}/${d},0))`, v: v(Math.round(r1.cover)) },
      { f: `IF(${blank},"",MAX(0,ROUNDUP((${e(C.stock)}+${e(C.transit)}-${e(R.pc)})/${d},0)))`, v: v(r1.daysToOrder), s: RES },
      { f: `IF(${blank},"",MAX(0,ROUNDUP(${d}*${e(C.lead)}+${e(R.ss)}+${d}*(1+${e(C.season)}/100)*${e(C.cycle)}-MIN(${e(C.stock)}+${e(C.transit)},${e(R.pc)}),0)))`, v: v(r1.qtyRaw) },
      { f: `IF(${blank},"",IF(${e(R.raw)}<=0,0,ROUNDUP(MAX(${e(R.raw)},${e(C.moq)})/MAX(1,${e(C.pack)}),0)*MAX(1,${e(C.pack)})))`, v: v(r1.qty), s: RES },
      { f: `IF(${blank},"",IF((${e(C.stock)}+${e(C.transit)})/${d}<${e(C.lead)},"Rupture probable",IF(${e(R.days)}<=7,"Commander cette semaine",IF(${e(C.stock)}/${d}>${e(C.lead)}+${e(C.cycle)}+90,"Surstock","OK"))))`, v: first ? statut(r1) : '' },
      { f: `IF(OR(${blank},${e(C.cost)}=""),"",${e(R.ss)}*${e(C.cost)})`, v: first && r1.cost ? r1.safetyValue : '' }
    );
    rows.push(cells);
  }
  return { name: 'Catalogue', freeze: true, widths: [24, ...Array(SEM).fill(8), 11, 10, 10, 10, 10, 11, 10, 10, 10, 10, 9, 10, 10, 11, 10, 11, 11, 11, 11, 10, 11, 22, 13], rows };
}

const GUIDE = [
  [{ v: 'Modèle de réappro · autoria.dev', s: TITLE }],
  [],
  [{ v: 'Onglet « Votre produit »', s: B }],
  ['Les chiffres saisis dans le calculateur, avec toutes les formules. Modifiez les cases en bleu clair : le reste se recalcule.'],
  ['Stock de sécurité = z × √(L × σ² + d² × σL²) · Point de commande = d × L + stock de sécurité.'],
  ['d : ventes moyennes par jour, tous canaux · σ : leur écart-type, hors B2B · L : délai total · σL : écart-type du délai, (délai le plus long − le plus court) ÷ 3 · z : niveau de service.'],
  ['Le B2B compte dans la moyenne, pas dans la variabilité : ses grosses commandes sont prévues, pas aléatoires.'],
  [],
  [{ v: 'Onglet « Catalogue »', s: B }],
  [`Jusqu'à ${CATALOGUE_LIGNES} produits. Pour chacun : ${SEM} semaines de ventes Amazon + site, le B2B moyen par semaine, le délai, le stock et vos règles.`],
  ['Triez par « Statut » chaque lundi : « Rupture probable » d\'abord (le stock, même avec l\'en-cours, ne tiendra pas jusqu\'à une nouvelle livraison), puis « Commander cette semaine ».'],
  [`La première ligne reprend votre produit (ses ${SEM} dernières semaines) : remplacez-la ou gardez-la comme exemple.`],
  [],
  [{ v: 'Bien remplir', s: B }],
  ['Stock possédé : entrepôt + Amazon (disponible, réservé, en transfert) + envois en route vers Amazon. Commandé, pas reçu : ce qui est commandé au fournisseur.'],
  ['Délai le plus court et le plus long : sur vos dix dernières commandes, du bon de commande à la mise en vente. Le Nouvel An chinois ajoute environ trois semaines aux commandes passées de décembre à février.'],
  ['Niveau de service : 95 % pour la plupart des produits (une rupture tous les vingt cycles de commande), 98 à 99 % pour les best-sellers, 90 % pour le fonds de catalogue.'],
  ['Retirez les semaines de rupture : des ventes à zéro faute de stock faussent la moyenne et l\'écart-type. Au-delà de 13 semaines, la saison se mélange à la variabilité.'],
  [],
  [{ v: 'Ce que ce modèle ne fait pas', s: B }],
  ['Il travaille sur des moyennes. La hausse saisonnière ne vaut que pour la période couverte par la commande : si elle commence avant la livraison, relevez aussi le délai ou le stock de sécurité.'],
  ['Il ne remplit pas de conteneur, ne répartit pas le stock entre Amazon et votre entrepôt, et ne voit pas venir une rupture fournisseur.'],
  [],
  [{ v: 'Sur des centaines de références', s: B }],
  ['Le même calcul, sur tout le catalogue et chaque semaine, avec Amazon et Shopify branchés directement : https://autoria.dev/reappro-prevision-stock'],
  ['Une question sur le modèle ? Écrivez à matteo@autoria.dev.']
];

// Le classeur complet, en octets.
export function modeleStock(p) {
  const r = compute(p);
  if (r.error) throw new Error(r.error);
  return buildXlsx([produit(p, r), catalogue(p), { name: "Mode d'emploi", widths: [130], rows: GUIDE }]);
}
